import { GetSellerPayLinkFeeContextResponse } from '@iwoca/lapi-client/iwocapay';
import { AlertBox } from '@iwoca/orion';

import Tag from './TagIcon.svg';
import { usePayLinkFeeContext } from '../../hooks/usePayLinkFeeContext.hook';
import { INDEFINITE_END_DATE } from '../../utils/constants';
import { PromotionalRatesModal } from '../PromotionalRates/PromotionalRates';
import { VolumePricingModal } from '../VolumePricingModal/VolumePricingBreakdown';

const getBestActivePromotion = (
  promotions: GetSellerPayLinkFeeContextResponse['data']['promotional']['active'],
  product: 'THREE_MONTHS' | 'TWELVE_MONTHS',
) => {
  const filteredPromotions = promotions.filter(
    (promotion) => promotion.product === product,
  );

  if (filteredPromotions.length === 0) return null;

  return filteredPromotions.reduce((min, promotion) =>
    promotion.fee_percentage < min.fee_percentage ? promotion : min,
  );
};

export const formatDate = (
  dateString: string,
  yearStyle: 'short' | 'long' = 'long',
) => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  if (yearStyle === 'short') {
    return `${day}/${month}/${String(year).slice(-2)}`;
  }
  return `${day}/${month}/${year}`;
};

const FeePeriod = ({
  feePercentage,
  start,
  end,
}: {
  feePercentage: number;
  start: string;
  end: string;
}) => {
  const isFeeIndefinite = INDEFINITE_END_DATE === end;

  if (isFeeIndefinite)
    return (
      <div>
        {feePercentage}% from start {formatDate(start)}
      </div>
    );

  return (
    <div>
      {feePercentage}% ({formatDate(start)} - {formatDate(end)})
    </div>
  );
};

export const RateContextHeader = () => {
  const { data: feeContext, isLoading: loadingFeeContext } =
    usePayLinkFeeContext();

  if (loadingFeeContext) return null;
  if (!feeContext?.data) return null;

  const activePromotions = feeContext.data.promotional.active;
  const hasActivePromotion = activePromotions.length > 0;

  const bestActivePromotions = {
    threeMonths: getBestActivePromotion(activePromotions, 'THREE_MONTHS'),
    twelveMonths: getBestActivePromotion(activePromotions, 'TWELVE_MONTHS'),
  };

  return (
    <>
      <div className="col-span-2">
        {hasActivePromotion && (
          <AlertBox className="mb-l !border-success-mid !bg-success-lt">
            <div className="flex items-center sm:flex-col sm:items-start">
              <div className="mr-l flex items-center text-xs font-bold">
                <img
                  height="16"
                  width="16"
                  alt="Tag"
                  src={Tag}
                  className="mr-s"
                />
                Promotional rate active
              </div>
              {bestActivePromotions.threeMonths && (
                <div className="mr-l flex text-xs font-reg">
                  <div>3 months</div>
                  <div className="px-[6px]">|</div>
                  <FeePeriod
                    feePercentage={
                      bestActivePromotions.threeMonths.fee_percentage
                    }
                    start={bestActivePromotions.threeMonths.start}
                    end={bestActivePromotions.threeMonths.end}
                  />
                </div>
              )}
              {bestActivePromotions.twelveMonths && (
                <div className="flex text-xs font-reg">
                  <div>12 months</div>
                  <div className="px-[6px]">|</div>
                  <FeePeriod
                    feePercentage={
                      bestActivePromotions.twelveMonths.fee_percentage
                    }
                    start={bestActivePromotions.twelveMonths.start}
                    end={bestActivePromotions.twelveMonths.end}
                  />
                </div>
              )}
            </div>
          </AlertBox>
        )}
      </div>
      <div className="col-span-1 flex items-center gap-m text-s font-reg sm:col-span-2 sm:flex-col sm:items-start">
        <div className="flex gap-m">
          <div>Contractual rate:</div>
          <div>
            3 months | {feeContext.data.contractual.THREE_MONTHS.fee_percentage}
            %
          </div>
          <div>
            12 months |{' '}
            {feeContext.data.contractual.TWELVE_MONTHS.fee_percentage}%
          </div>
          {feeContext.data.contractual.THIRTY_DAYS && (
            <div>
              30 days | {feeContext.data.contractual.THIRTY_DAYS.fee_percentage}
              %
            </div>
          )}
        </div>
        <div className="flex gap-l">
          <VolumePricingModal />
          <PromotionalRatesModal />
        </div>
      </div>
    </>
  );
};
